/** English source dictionary — every key exists here; other locales fall back to it. */

export const en = {
  'app.name': 'Traugily',
  'app.tagline': 'Steady progress, made simple',

  'nav.today': 'Today',
  'nav.plan': 'Plan',
  'nav.log': 'Log',
  'nav.progress': 'Progress',
  'nav.reports': 'Reports',
  'nav.calendar': 'Calendar',
  'nav.account': 'Account',

  'calendar.title': 'Calendar',
  'calendar.prev_month': 'Previous month',
  'calendar.next_month': 'Next month',
  'calendar.blocked': 'Unavailable',
  'calendar.no_workout': 'No workout on this day.',
  'calendar.begin_workout': 'Begin workout',
  'calendar.delete_workout': 'Delete workout',
  'calendar.redo_workout': 'Delete & redo workout',
  'calendar.delete_hint': 'Deleting a completed workout removes it from your history so you can do it again.',
  'calendar.deleted': 'Workout deleted.',
  'calendar.reset_for_redo': 'Workout reset — it is planned again and removed from your history.',
  'calendar.status.planned': 'Planned',
  'calendar.status.completed': 'Completed',
  'calendar.status.cancelled': 'Cancelled',
  'calendar.status.rest': 'Rest day',
  'nav.admin': 'Admin',

  'action.begin_workout': 'Begin workout',
  'action.resume_workout': 'Resume workout',
  'action.finish_workout': 'Finish workout',
  'action.save': 'Save',
  'action.cancel': 'Cancel',
  'action.confirm': 'Confirm',
  'action.retry': 'Try again',
  'action.back': 'Back',
  'action.next': 'Next',
  'action.change_password': 'Change password',
  'action.skip': 'Skip for now',
  'action.sign_in': 'Sign in',
  'action.sign_up': 'Create account',
  'action.sign_out': 'Sign out',
  'action.discard_workout': 'Discard workout',
  'action.keep_draft': 'Keep as draft',
  'action.undo': 'Undo',
  'action.download': 'Download',
  'action.generate_report': 'Generate report',
  'action.accept': 'Accept',
  'action.reject': 'Skip proposal',
  'action.edit': 'Edit',
  'action.close': 'Close',

  'state.loading': 'Loading…',
  'state.empty_title': 'Nothing here yet',
  'state.error_title': 'Something went wrong',
  'state.offline': 'You are offline. Your changes are saved on this device.',
  'state.online': 'Back online. Syncing your changes…',
  'state.saved_locally': 'Saved locally',
  'state.waiting_to_sync': 'Waiting to sync',
  'state.synced': 'Synced',
  'state.sync_needs_attention': 'Sync needs attention',
  'state.conflict_title': 'Workout changed on another device',
  'state.conflict_body':
    'Nothing was overwritten. Choose which version to keep — both records stay available.',
  'state.conflict_keep_local': 'Keep this device’s changes',
  'state.conflict_keep_remote': 'Keep the other device’s version',
  'state.conflict_discard_confirm': 'Yes, discard my unsynced entries',

  'today.title': 'Today',
  'today.next_action': 'Your next step',
  'today.no_session': 'No session planned today',
  'today.rest_day': 'Rest day — recovery is part of the plan',
  'today.pending_proposals': '{count} plan suggestions waiting for you',
  'today.unread_notifications': '{count} unread notifications',
  'today.start_onboarding': 'Set up your profile',
  'today.action.start_workout': 'Start today’s workout',
  'today.action.resume_workout': 'Resume your workout',
  'today.action.view_plan': 'View your plan',
  'today.action.start_onboarding': 'Finish setup',
  'today.action.no_workout_today': 'No workout today — rest well.',
  'today.action.generate_plan': 'Generate your plan',
  'today.plan_summary': 'Week goal: {sessions} sessions · {minutes} min each',

  'plan.title': 'Your plan',
  'plan.phase': 'Current phase',
  'plan.phase_remaining': '{weeks} weeks remaining',
  'plan.why': 'Why this plan',
  'plan.assumptions': 'Assumptions',
  'plan.rest_days': 'Rest days each week: {count}',
  'plan.weekly_rhythm': 'Weekly rhythm',
  'plan.week.training': 'Training',
  'plan.week.rest': 'Rest / recovery',
  'plan.accept_prompt': 'Review and accept your plan to start training.',
  'plan.accept_confirm':
    'I understand this is not a medical service and will seek professional help for pain or injury.',
  'plan.accepted': 'Plan activated',
  'plan.length_change': 'Change plan length',
  'plan.length_preview': 'Review the before/after comparison before confirming.',
  'plan.length_before': 'Before',
  'plan.length_after': 'After',
  'plan.version': 'Plan version {version}',
  'plan.phase.orientation.title': 'Orientation',
  'plan.phase.orientation.purpose': 'Learn the movements, build the habit, keep it easy.',
  'plan.phase.build_up.title': 'Build-up',
  'plan.phase.build_up.purpose': 'Gradually add volume while your recovery stays steady.',
  'plan.phase.progression.title': 'Progression',
  'plan.phase.progression.purpose': 'Small, earned increases in load or reps.',
  'plan.phase.consolidation.title': 'Consolidation',
  'plan.phase.consolidation.purpose': 'Settle the work in, recover well, keep the routine.',
  'plan.phase.review.title': 'Review',
  'plan.phase.review.purpose': 'Look back, adjust, and choose what comes next.',
  'plan.session.full_body.title': 'Full body',
  'plan.session.full_body.focus': 'Squat, push, pull, hinge, core',
  'plan.session.push.title': 'Push day',
  'plan.session.push.focus': 'Chest, shoulders, triceps, core',
  'plan.session.pull.title': 'Pull day',
  'plan.session.pull.focus': 'Back, biceps, core',
  'plan.session.legs.title': 'Leg day',
  'plan.session.legs.focus': 'Quads, hamstrings, calves',
  'plan.session.cardio.title': 'Conditioning',
  'plan.session.cardio.focus': 'Steady or interval cardio',
  'plan.violation.min_rest_days': 'Plan needs at least {required} rest day(s) per week.',
  'plan.violation.consecutive_heavy': 'Heavy sessions for {muscles} fall on consecutive days.',
  'plan.violation.excluded_present': 'Excluded exercise {exercise} appears in the plan.',

  'log.title': 'Workout log',
  'log.history': 'Past workouts',
  'log.no_history': 'No workouts yet — your first one starts from Today.',
  'log.planned': 'Planned',
  'log.actual': 'Actual',
  'log.rest_timer': 'Rest timer',
  'log.rest_paused': 'Rest paused',
  'log.skip_reason': 'Why are you skipping this?',
  'log.skip.no_reason': 'No reason (unknown)',
  'log.skip.time': 'Not enough time',
  'log.skip.equipment_unavailable': 'Equipment busy or unavailable',
  'log.skip.discomfort': 'Discomfort or pain',
  'log.skip.too_difficult': 'Too difficult today',
  'log.skip.preference': 'Personal preference',
  'log.skip.other': 'Other',
  'log.report_pain': 'Report discomfort or pain',
  'log.pain.discomfort': 'Mild discomfort',
  'log.pain.pain': 'Pain',
  'log.pain.urgent': 'Urgent — dizziness, chest pain, fainting',
  'log.pain_note': 'Anything else you want to add? (optional)',
  'log.finish_summary': 'Session summary',
  'log.duration': 'Duration',
  'log.active_time': 'Active time (estimate)',
  'log.total_rest': 'Total rest',
  'log.completed_exercises': 'Exercises completed',
  'log.skipped_exercises': 'Exercises skipped',
  'log.volume': 'Estimated volume',
  'log.calories': 'Estimated calorie burn',
  'log.calories_note': 'Estimate only — not a medical measurement.',
  'log.review_before_save': 'Review before saving',
  'log.energy': 'Energy',
  'log.difficulty': 'Difficulty',
  'log.recovery': 'Feeling recovered',
  'log.general_note': 'General note (optional)',
  'log.apply_change': 'Apply this change to…',
  'log.apply.this_set': 'This set only',
  'log.apply.remaining_sets': 'Remaining sets',
  'log.apply.note_only': 'Only as a note',
  'log.no_executed_sets': 'Finish needs at least one logged set. Keep it as a draft or discard it.',
  'log.set_empty': 'Enter reps, weight, RPE or RIR before saving — empty sets are not saved.',
  'log.set_saved': 'Set saved.',
  'log.set_queued_offline': 'You are offline — the set is saved on this device and will sync.',
  'log.finish_failed': 'Could not finish the workout. Please try again.',

  'safety.guidance.urgent':
    'Stop training now. If you have chest pain, fainting, dizziness, sudden shortness of breath or similar symptoms, seek urgent medical help immediately. Traugily cannot assess your symptoms.',
  'safety.guidance.pain':
    'Thanks for telling us. Please see a qualified healthcare professional before resuming this movement. We will not increase this exercise until you choose a safe next step.',
  'safety.guidance.discomfort':
    'Mild discomfort happens. Reduce the load or range, and stop if it worsens or persists. Mention it to a professional if it keeps coming back.',
  'safety.guidance.recurrence':
    'This area has come up more than once recently. Please get it assessed by a qualified healthcare professional before continuing this movement.',
  'safety.guidance.none': 'Thanks for the report.',

  'progress.title': 'Progress',
  'progress.weigh_in': 'Weekly weigh-in',
  'progress.weigh_in_hint':
    'For consistent readings, weigh after using the toilet and before breakfast, around the same time each week.',
  'progress.weigh_in_skip': 'Skip this week',
  'progress.weight': 'Weight',
  'progress.trend': 'Trend (moving average)',
  'progress.trend_note': 'Trends are descriptive — not a diagnosis.',
  'progress.consistency': 'Consistency',
  'progress.personal_records': 'Personal records',
  'progress.goal_review': 'Goal review',
  'progress.goal_review_interpretation':
    'This compares your direction with your goal — it does not promise a result.',

  'nutrition.title': 'Nutrition',
  'nutrition.targets': 'Daily targets (estimates)',
  'nutrition.targets.estimate_notice':
    'Targets are estimates based on your profile, adjusted gradually by phase. They are not medical advice.',
  'nutrition.suggestions': 'Meal ideas',
  'nutrition.suggestions.estimate_notice':
    'Calorie values are coarse estimates. You can log meals by description without exact numbers.',
  'nutrition.log_meal': 'Log a meal',
  'nutrition.adjustment.within_week':
    'Based on this week’s logs, a small optional adjustment could be about {suggestedDailyKcal} kcal/day. Nothing is changed automatically.',

  'account.title': 'Account',
  'account.profile': 'Profile',
  'account.billing': 'Billing',
  'account.privacy': 'Privacy & data',
  'account.language': 'Language',
  'account.units': 'Units',
  'account.sign_out': 'Sign out',
  'account.export_data': 'Export my data',
  'account.delete_account': 'Delete account',
  'account.delete_confirm': 'Type your email to confirm deletion:',
  'account.entitlement': 'Your plan',
  'account.plan.free': 'Free',
  'account.plan.premium': 'Premium',
  'account.subscribe': 'Subscribe — about €5/month',
  'account.manage_billing': 'Manage billing',
  'account.invoices': 'Invoices',
  'account.next_bill': 'Next bill',
  'account.cancel_terms': 'Cancel anytime from the billing portal. Your data stays exportable.',
  'account.marketing_consent': 'Send me product news and offers',
  'account.photo_consent': 'Allow progress photo uploads',
  'account.ai_consent': 'Allow AI-drafted explanations for my plan (never diagnoses)',

  'account.change_password': 'Change password',
  'account.change_password_heading': 'Change password',
  'account.password_current': 'Current password',
  'account.password_new': 'New password',
  'account.password_confirm': 'Confirm new password',
  'account.password_requirements': 'At least 10 characters, with upper, lower and number.',

  'account.password_changed': 'Password updated.',

  'account.error.old_password_incorrect': 'Current password is incorrect.',

  'billing.checkout_success':
    'Thanks! Your subscription activates once Stripe confirms the payment.',
  'billing.checkout_cancelled': 'No worries — you can subscribe whenever you are ready.',
  'billing.webhook_notice': 'Entitlements update only after Stripe confirms a payment event.',

  'reports.title': 'Reports',
  'reports.period': 'Period',
  'reports.detail': 'Detail level',
  'reports.detail.summary': 'Summary',
  'reports.detail.standard': 'Standard',
  'reports.detail.detailed': 'Detailed',
  'reports.status.pending': 'Pending',
  'reports.status.processing': 'Generating',
  'reports.status.ready': 'Ready',
  'reports.status.failed': 'Failed',
  'reports.download': 'Download PDF',
  'reports.estimate_notice': 'All estimates in the report are labelled.',
  'reports.start_date': 'Start date',
  'reports.end_date': 'End date',
  'reports.language': 'Report language',
  'reports.history': 'Past reports',
  'reports.empty': 'No reports yet',
  'reports.empty_hint': 'Pick a period above and generate your first PDF.',
  'reports.polling': 'Generating your PDF — checking status…',
  'reports.error_dates_required': 'Pick a start and end date.',
  'reports.error_date_order': 'End date must be on or after start date.',

  'error.network': 'We could not reach the server. Your local changes are safe.',
  'error.validation': 'Please check the highlighted fields.',
  'error.permission': 'You do not have permission for this action.',
  'error.generic': 'Something went wrong. Please try again.',
  'offline.banner': 'Offline mode — everything you log is stored on this device first.',
  'offline.sync_now': 'Sync now',

  'onboarding.title': 'Let’s set up your plan',
  'onboarding.step': 'Step {current} of {total}',
  'onboarding.save_resume': 'Progress saves as you go — you can come back any time.',
  'onboarding.basics': 'About you',
  'onboarding.goals': 'Your goals',
  'onboarding.training': 'Training',
  'onboarding.lifestyle': 'Lifestyle',
  'onboarding.nutrition': 'Food preferences',
  'onboarding.safety': 'Safety first',
  'onboarding.safety_ack':
    'I understand Traugily is not a medical service and will seek professional help for pain or injury.',
  'onboarding.safety_ack_help':
    'Required: this keeps the product honest about what it can and cannot do.',
  'onboarding.realistic_range':
    'Most people need 8–16 weeks to see meaningful change. Shorter plans adjust focus — they never promise faster results.',
  'onboarding.goal.muscle_gain': 'Build muscle',
  'onboarding.goal.fat_loss': 'Lose fat',
  'onboarding.goal.weight_loss': 'Lose weight',
  'onboarding.goal.fitness': 'Improve fitness',
  'onboarding.goal.strength': 'Get stronger',
  'onboarding.goal.general_health': 'General health',
  'onboarding.goal.consistent_routine': 'Build a consistent routine',
  'onboarding.experience.new': 'New to training',
  'onboarding.experience.returning': 'Returning after a break',
  'onboarding.experience.experienced': 'Experienced',

  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.confirm_password': 'Confirm password',
  'auth.forgot': 'Forgot your password?',
  'auth.reset_sent': 'If that email exists, a reset link is on its way.',
  'auth.verify_notice': 'Please verify your email — the link is in your inbox.',
  'auth.google': 'Continue with Google',
  'auth.apple': 'Continue with Apple',
  'auth.have_account': 'Already have an account?',
  'auth.no_account': 'New to Traugily?',

  'admin.title': 'Admin',
  'admin.health': 'Operational health',
  'admin.exercises': 'Exercises',
  'admin.flags': 'Feature flags',
  'admin.members': 'Members',
  'admin.audit': 'Audit log',
  'admin.rules': 'Rules & prompts',
  'admin.no_access': 'You do not have access to this area.',
  'admin.grant_premium': 'Grant premium',
  'admin.revoke_to_free': 'Move to free',

  'planner.conflict.blocked_date': 'That date is marked as unavailable.',
  'planner.conflict.no_rest_between': 'That would leave no rest day between sessions.',
  'planner.conflict.training_streak': 'That would make {streak} training days in a row.',
  'planner.move.review_before_confirm': 'Please review these conflicts before moving the session.',
  'planner.rest_day.requested': 'Rest day added for that date.',
  'planner.availability.updated_recalc_hint':
    'Availability updated. Generate a new plan to apply it.',

  'catalog.replacements.found': 'Here is a comparable alternative.',
  'catalog.replacements.no_safe_alternative':
    'No direct alternative from your equipment fits safely. You can skip this exercise or change your equipment settings.',

  'adaptation.explain.missed_targets':
    'You have struggled to hit the planned targets for {exercise} recently ({missedSessions} of {observedSessions} sessions). We suggest keeping or slightly lowering the load while you build the movement.',
  'adaptation.explain.comfortable_load':
    'You completed {exercise} comfortably across {comfortableSessions} sessions. A small increase of {percent}% in one dimension is reasonable.',
  'adaptation.explain.comfortable_reps':
    'You completed {exercise} comfortably across {comfortableSessions} sessions. One extra rep per set is a reasonable next step.',
  'adaptation.explain.safety_block':
    'Because you reported an issue with {exercise}, we are not suggesting any increase. Choose a safe next step when you are ready.',
  'adaptation.explain.skipped':
    'You skipped {exercise} {times} times. We can swap it for a comparable movement or move it to another day.',
  'adaptation.explain.missed_workout':
    'You missed {missedCount} sessions. We can rebalance this week while keeping your rest days.',
  'adaptation.explain.weight_trend':
    'Your weight trend moved {deltaKg} kg {direction} over recent weeks. This is one signal among several — a gradual review is suggested, not a conclusion.',

  'install.title': 'Install Traugily',
  'install.body': 'Add Traugily to your home screen for quick access and offline workouts.',
  'install.ios_hint': 'On iOS: Share → Add to Home Screen.',
  'install.dismiss': 'Not now',
} as const;

export type EnDictionary = typeof en;
